import React, { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query"
import Button from "~/components/ui/Button"
import Icon from "~/components/ui/Icon"
import { sendEvent } from "~/sdk/analytics"
import { useSignal } from "~/sdk/signal"
import { capitalize } from "~/sdk/utils/capitalize"
import Image from "~/components/ui/Image"
import { Product } from "@decocms/apps-commerce/types"
import { invoke } from "~/runtime"
import { debounce } from "~/sdk/debounce"

export interface Props {
    /**
     * @title Placeholder
     * @description Placeholder padrão da barra de busca
     * @default O que você procura?
     */
    placeholder?: string

    /**
     * @ignore
     */
    isMobile?: boolean
}

interface SuggestionSearchItem {
    term: string
    href: string
}

function Searchbar({ placeholder = 'O que você procura?', isMobile = false }: Props) {
    const isInputFocused = useSignal(false)
    const loadingClick = useSignal(false)

    // inputValue: controlled input (immediate); queryTerm: debounced query key
    const [inputValue, setInputValue] = useState('')
    const [queryTerm, setQueryTerm] = useState('')

    const debouncedSetQueryTerm = useMemo(
        () => debounce((v: string) => setQueryTerm(v), 250),
        [],
    )

    const { data: suggestionsData, isFetching } = useQuery({
        queryKey: ['suggestions', queryTerm],
        queryFn: () => invoke.vtex.loaders.legacy.suggestions({ query: queryTerm, count: 12 }),
        enabled: queryTerm.length > 0,
        staleTime: 30_000,
    })

    const { products = [], searches = [] } = suggestionsData ?? {}
    const notFound = !(products?.length || searches.length)

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setInputValue(value)
        debouncedSetQueryTerm(value)
    }, [debouncedSetQueryTerm])

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (inputValue) {
            loadingClick.value = true
            sendEvent({
                name: 'search',
                params: { search_term: inputValue },
            })
            const query = inputValue.replace(/["'\$]/g, '').toLowerCase()
            location.href = `/s?q=${encodeURIComponent(query)}`
        }
    }

    const constructSearchUrl = useCallback((href: string) => {
        if (!href) return null
        const baseUrl = self.location.origin
        const path = href.replace(
            /^https:\/\/(miess\.vtexcommercestable\.com\.br|secure\.miess\.com\.br)/,
            '',
        )
        return `${baseUrl}${path}?map=c,ft`
    }, [])

    const SuggestionSearchItem = ({ term, href }: SuggestionSearchItem) => {
        const searchUrl = constructSearchUrl(href)
        if (!searchUrl) return null
        return (
            <li className='py-1 px-2 w-full hover:translate-x-3 transition-transform'>
                <a href={searchUrl} className='w-full block'>{term}</a>
            </li>
        )
    }

    return (
        <div className='w-full text-sm relative'>
            <form
                id='search-form'
                className='border-b-2 flex border-white relative w-full'
                onSubmit={handleSubmit}
            >
                <input
                    id='search-input'
                    className='h-12 placeholder-shown:text-ellipsis px-3 placeholder:text-white bg-transparent flex-grow'
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={() => isInputFocused.value = true}
                    onBlur={() => setTimeout(() => isInputFocused.value = false, 200)}
                    aria-label={placeholder}
                    placeholder={placeholder}
                    autoComplete='off'
                />

                {/* text-white: no DaisyUI 5 o `.btn` pinta o texto com a cor base (escura),
                    e a lupa saía preta sobre o fundo vinho. No DaisyUI 4 do Fresh o
                    `btn-ghost` herdava o branco do header. */}
                <Button
                    type='submit'
                    className='btn-ghost btn-square text-white disabled:bg-transparent disabled:text-white absolute right-0 h-full'
                    aria-label='Pesquisar'
                    tabIndex={-1}
                >
                    {isFetching ? <span className='loading loading-spinner w-[30px] h-[30px]' /> : (
                        <>
                            {loadingClick.value
                                ? <span className='loading loading-spinner w-[30px] h-[30px]' />
                                : <Icon id='MagnifyingGlass' size={isMobile ? 36 : 24} />}
                        </>
                    )}
                </Button>
            </form>

            {!notFound && isInputFocused.value && (
                <div className='overflow-y-auto overflow-x-hidden max-h-[calc(100vh-110px)] absolute left-0 right-0 top-[calc(100%+12px)] z-[1] bg-white text-black shadow-xl lg:w-[280px]'>
                    <ul className='flex flex-col'>
                        {(searches as SuggestionSearchItem[]).map((searchItem) => (
                            <SuggestionSearchItem key={searchItem.href} {...searchItem} />
                        ))}
                        {(products as Product[]).map(({ url, image, isVariantOf, name }) => (
                            <li key={url} className='py-1 px-2 w-full hover:translate-x-3 transition-transform'>
                                <a href={url} className='w-full flex items-center gap-2'>
                                    <Image
                                        height={24}
                                        width={24}
                                        src={image?.[0]?.url ?? ''}
                                        loading='lazy'
                                    />
                                    {capitalize(
                                        (isVariantOf?.name ?? name ?? 'Produto Desconhecido').toLowerCase(),
                                    )}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    )
}

export default Searchbar
